import { DEFAULT_AVATARS, getDefaultAvatarUrl } from './defaultAvatars';

export interface DefaultAvatarPickerProps {
  /** Currently selected avatar id. */
  value: string | null;
  /** Fired when the player taps a tile. */
  onChange: (id: string) => void;
  /** Tile size in CSS px. Default 64 — twelve tiles fit in a 4×3 grid
   * inside a typical phone-width modal. */
  tileSize?: number;
}

/**
 * Grid of the twelve commissioned default-avatar PNGs. Tapping a tile
 * fires {@link DefaultAvatarPickerProps#onChange} with the avatar id;
 * the host stores the id wherever it tracks the player's identity
 * (currently {@link usePlayerAvatar} → localStorage; later moves to
 * the server-side profile).
 *
 * <p>Selected tile gets a glowing ring; unselected tiles dim slightly
 * so the focus reads. The grid is keyboard-accessible (Tab through,
 * Enter / Space to pick).
 */
export function DefaultAvatarPicker({
  value,
  onChange,
  tileSize = 64,
}: DefaultAvatarPickerProps) {
  return (
    <div
      role="radiogroup"
      aria-label="Choose your avatar"
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: 10,
        justifyItems: 'center',
        maxWidth: tileSize * 4 + 30,
        margin: '0 auto',
      }}
    >
      {DEFAULT_AVATARS.map((avatar) => {
        const selected = value === avatar.id;
        const url = getDefaultAvatarUrl(avatar.id, 'idle');
        if (!url) return null;
        return (
          <button
            key={avatar.id}
            type="button"
            role="radio"
            aria-checked={selected}
            aria-label={`Avatar ${avatar.id}`}
            onClick={() => onChange(avatar.id)}
            style={{
              width: tileSize,
              height: tileSize,
              padding: 0,
              border: 'none',
              borderRadius: '50%',
              cursor: 'pointer',
              background: selected
                ? 'radial-gradient(circle at center, rgba(255,213,128,0.55) 0%, rgba(255,213,128,0) 70%)'
                : 'transparent',
              boxShadow: selected
                ? '0 0 0 3px #FFD580, 0 0 16px rgba(255,213,128,0.6)'
                : '0 2px 8px rgba(0,0,0,0.35)',
              opacity: selected ? 1 : 0.85,
              transition: 'opacity 120ms, box-shadow 120ms',
            }}
          >
            <img
              src={url}
              alt=""
              draggable={false}
              style={{
                width: '100%',
                height: '100%',
                borderRadius: '50%',
                objectFit: 'cover',
                userSelect: 'none',
                pointerEvents: 'none',
              }}
            />
          </button>
        );
      })}
    </div>
  );
}
